import { api } from './api';
import { Idea, IdeaDetail, IdeasQueryParams, CreateIdeaPayload, UpdateIdeaPayload } from '../../types/idea.types';
import { IdeaStop, IdeaStopType, IdeaStopDataUnion, UpdateIdeaStopPayload } from '../../types/ideaStop.types';
import {
  FlightFilterOptionsResponse,
  FlightSearchPayload,
  FlightSearchResult,
  FlightToIdeaPayload,
  FlightToIdeaResponse,
  type FlightSearchRequest,
} from '../../types/flight.types';
import { HotelSearchPayload, HotelSearchResult } from '../../types/hotel.types';
import { TourSearchPayload, TourSearchResult, TourToIdeaPayload, TourToIdeaResponse } from '../../types/tour.types';
import {
  PlaceSearchResult,
  PlaceSearchRequest,
  PlaceTypeOption,
  PlaceToIdeaPayload,
  PlaceToIdeaResponse,
} from '../../types/place.types';
import type { PaginatedSearchRequest } from '../../types/pagination.types';
import { buildFlightSearchParams, serializeFlightSearchParams } from '../../utils/flightSearchQueryParams';
import { buildPlaceSearchParams, serializePlaceSearchParams } from '../../utils/placeSearchQueryParams';

export interface CreateIdeaStopApiPayload {
  type: IdeaStopType;
  ideaStopData: IdeaStopDataUnion & { idea: number };
}

export interface IdeaStopResponse {
  id: number;
  stop_type: IdeaStopType;
  idea: number;
  description: string;
  start_date_time: string;
  email?: string;
  phone?: string;
  link?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

export const ideasApi = api.injectEndpoints({
  endpoints: builder => ({
    getIdeas: builder.query<Idea[], IdeasQueryParams>({
      query: (params = {}) => {
        const queryParams: Record<string, string> = {};

        if (params.ordering) queryParams.ordering = params.ordering;
        if (params.search) queryParams.search = params.search;

        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/`,
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      providesTags: ['Idea'],
      keepUnusedDataFor: 5,
    }),

    getIdea: builder.query<IdeaDetail, string>({
      query: (id: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/${id}/`,
        };
      },
      providesTags: (result, error, id) => [{ type: 'Idea', id }],
    }),

    createIdea: builder.mutation<Idea, CreateIdeaPayload>({
      query: ideaData => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/`,
          method: 'POST',
          body: ideaData,
        };
      },
      invalidatesTags: ['Idea'],
    }),

    updateIdea: builder.mutation<Idea, { id: number; data: UpdateIdeaPayload }>({
      query: ({ id, data }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/${id}/`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => ['Idea', { type: 'Idea', id: arg.id.toString() }],
    }),

    deleteIdea: builder.mutation<void, string>({
      query: (id: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/${id}/`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Idea'],
    }),

    uploadIdeaCustomCover: builder.mutation<Idea | void, { ideaId: number; file: File }>({
      query: ({ ideaId, file }) => {
        const formData = new FormData();
        formData.append('image', file);
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/${ideaId}/custom-cover/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => ['Idea', { type: 'Idea', id: String(arg.ideaId) }],
    }),

    deleteIdeaCustomCover: builder.mutation<void, number>({
      query: ideaId => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/${ideaId}/custom-cover/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, ideaId) => ['Idea', { type: 'Idea', id: String(ideaId) }],
    }),

    uploadIdeaStopCustomCover: builder.mutation<
      IdeaStop | void,
      { ideaStopId: number; file: File; ideaId?: number }
    >({
      query: ({ ideaStopId, file }) => {
        const formData = new FormData();
        formData.append('image', file);
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/idea-stops/${ideaStopId}/custom-cover/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        'IdeaStop',
        { type: 'IdeaStop', id: String(arg.ideaStopId) },
        { type: 'IdeaStop', id: 'LIST' },
        ...(typeof arg.ideaId === 'number' ? [{ type: 'Idea', id: String(arg.ideaId) } as const] : []),
      ],
    }),

    deleteIdeaStopCustomCover: builder.mutation<void, { ideaStopId: number; ideaId?: number }>({
      query: ({ ideaStopId }) => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/idea-stops/${ideaStopId}/custom-cover/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [
        'IdeaStop',
        { type: 'IdeaStop', id: String(arg.ideaStopId) },
        { type: 'IdeaStop', id: 'LIST' },
        ...(typeof arg.ideaId === 'number' ? [{ type: 'Idea', id: String(arg.ideaId) } as const] : []),
      ],
    }),

    getIdeaStops: builder.query<IdeaStop[], string>({
      query: (ideaId: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/idea-stops/${ideaId}`,
        };
      },
      providesTags: (result, error, ideaId) => [
        { type: 'IdeaStop', id: 'LIST' },
        { type: 'Idea', id: ideaId },
      ],
      keepUnusedDataFor: 5,
    }),

    getIdeaStop: builder.query<IdeaStop, string>({
      query: (id: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/idea-stops/${id}`,
        };
      },
      providesTags: (result, error, id) => [{ type: 'IdeaStop', id }],
    }),

    createIdeaStop: builder.mutation<IdeaStopResponse, CreateIdeaStopApiPayload>({
      query: ({ type, ideaStopData }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/${ideaStopData.idea}/idea-stops`,
          method: 'POST',
          params: { type },
          body: ideaStopData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        'IdeaStop',
        { type: 'IdeaStop', id: 'LIST' },
        { type: 'Idea', id: arg.ideaStopData.idea.toString() },
      ],
    }),

    updateIdeaStop: builder.mutation<IdeaStop, UpdateIdeaStopPayload>({
      query: ({ id, data }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/idea-stops/${id}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: (result, error, arg) => [
        'IdeaStop',
        { type: 'IdeaStop', id: arg.id.toString() },
        { type: 'IdeaStop', id: 'LIST' },
      ],
    }),

    deleteIdeaStop: builder.mutation<void, string>({
      query: (id: string) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/idea-stops/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, id) => ['IdeaStop', { type: 'IdeaStop', id }, { type: 'IdeaStop', id: 'LIST' }],
    }),

    flightSearchFilterOptions: builder.mutation<FlightFilterOptionsResponse, FlightSearchPayload>({
      query: payload => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/flight-search/filter-options`,
        method: 'POST',
        body: payload,
      }),
    }),

    flightSearch: builder.mutation<FlightSearchResult, FlightSearchRequest>({
      query: ({ payload, page = 1, searchFilters }) => {
        const queryString = serializeFlightSearchParams(buildFlightSearchParams(page, searchFilters));

        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/flight-search${queryString ? `?${queryString}` : ''}`,
          method: 'POST',
          body: payload,
        };
      },
    }),

    saveFlightToIdea: builder.mutation<FlightToIdeaResponse, { ideaId: number; body: FlightToIdeaPayload }>({
      query: ({ ideaId, body }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/${ideaId}/flight-to-idea`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (result, error, arg) => [
        'Idea',
        { type: 'Idea', id: String(arg.ideaId) },
        { type: 'IdeaStop', id: 'LIST' },
        { type: 'IdeaStop', id: String(arg.ideaId) },
      ],
    }),

    savePlaceToIdea: builder.mutation<PlaceToIdeaResponse, { ideaId: number; body: PlaceToIdeaPayload }>({
      query: ({ ideaId, body }) => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/${ideaId}/place-to-idea`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'Idea',
        { type: 'Idea', id: String(arg.ideaId) },
        { type: 'IdeaStop', id: 'LIST' },
        { type: 'IdeaStop', id: String(arg.ideaId) },
      ],
    }),

    hotelSearch: builder.mutation<HotelSearchResult, PaginatedSearchRequest<HotelSearchPayload>>({
      query: ({ payload, page = 1 }) => {
        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/discover/hotels/search`,
          method: 'POST',
          body: payload,
          params: { page },
        };
      },
    }),

    getPlacesTypes: builder.query<PlaceTypeOption[], void>({
      query: () => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/places-type`,
      }),
    }),

    placeSearch: builder.mutation<PlaceSearchResult, PlaceSearchRequest>({
      query: ({ payload, page = 1, searchFilters, sortBy }) => {
        const queryString = serializePlaceSearchParams(buildPlaceSearchParams(page, searchFilters, sortBy));

        return {
          url: `${process.env.REACT_APP_API_URL}/ideas/place-search${queryString ? `?${queryString}` : ''}`,
          method: 'POST',
          body: payload,
        };
      },
    }),

    tourSearch: builder.mutation<TourSearchResult, PaginatedSearchRequest<TourSearchPayload>>({
      query: ({ payload, page = 1 }) => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/discover/tours/search`,
        method: 'POST',
        body: payload,
        params: { page },
      }),
    }),

    saveTourToIdea: builder.mutation<TourToIdeaResponse, { ideaId: number; body: TourToIdeaPayload }>({
      query: ({ ideaId, body }) => ({
        url: `${process.env.REACT_APP_API_URL}/ideas/${ideaId}/tour-to-idea`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        'Idea',
        { type: 'Idea', id: String(arg.ideaId) },
        { type: 'IdeaStop', id: 'LIST' },
        { type: 'IdeaStop', id: String(arg.ideaId) },
      ],
    }),
  }),
});

export const {
  useGetIdeasQuery,
  useGetIdeaQuery,
  useCreateIdeaMutation,
  useUpdateIdeaMutation,
  useDeleteIdeaMutation,
  useUploadIdeaCustomCoverMutation,
  useDeleteIdeaCustomCoverMutation,
  useUploadIdeaStopCustomCoverMutation,
  useDeleteIdeaStopCustomCoverMutation,
  useGetIdeaStopsQuery,
  useGetIdeaStopQuery,
  useLazyGetIdeaStopQuery,
  useCreateIdeaStopMutation,
  useUpdateIdeaStopMutation,
  useDeleteIdeaStopMutation,
  useFlightSearchFilterOptionsMutation,
  useFlightSearchMutation,
  useSaveFlightToIdeaMutation,
  useSavePlaceToIdeaMutation,
  useHotelSearchMutation,
  useGetPlacesTypesQuery,
  usePlaceSearchMutation,
  useTourSearchMutation,
  useSaveTourToIdeaMutation,
} = ideasApi;
